<script lang="ts">
  import { tick } from "svelte";
  import { BRIDGE_STATIONS } from "../../shared/corridor";
  import type { BridgeId, PublicBridge } from "../../shared/contracts";
  import { bridgePositionCopy } from "../format";

  export let bridges: PublicBridge[];
  export let selectedBridgeId: BridgeId = "brickell";
  export let onbridgeselect: ((bridgeId: BridgeId, trigger: HTMLButtonElement) => void) | undefined = undefined;

  let scroller: HTMLDivElement;
  $: if (scroller && selectedBridgeId) void revealSelected(selectedBridgeId);
  async function revealSelected(id: BridgeId): Promise<void> {
    await tick();
    const button = scroller?.querySelector<HTMLElement>(`[data-bridge-navigator-id="${id}"] button`);
    if (button) revealStation(button);
  }

  $: bridgeById = new Map(bridges.map((bridge) => [bridge.id, bridge]));

  function revealStation(station: HTMLElement): void {
    if (typeof station.scrollIntoView !== "function") return;
    station.scrollIntoView({ behavior: "auto", block: "nearest", inline: "nearest" });
  }

  function selectStation(bridgeId: BridgeId, trigger: HTMLButtonElement): void {
    revealStation(trigger);
    onbridgeselect?.(bridgeId, trigger);
  }

  function moveStationFocus(event: KeyboardEvent, index: number): void {
    const nextIndex = event.key === "ArrowLeft"
      ? index - 1
      : event.key === "ArrowRight"
        ? index + 1
        : event.key === "Home"
          ? 0
          : event.key === "End"
            ? BRIDGE_STATIONS.length - 1
            : null;
    if (nextIndex === null) return;
    event.preventDefault();
    const station = (event.currentTarget as HTMLElement).closest("ol")
      ?.querySelectorAll<HTMLElement>(".bridge-navigator__station-control")
      .item(Math.max(0, Math.min(BRIDGE_STATIONS.length - 1, nextIndex)));
    station?.focus();
    if (station) revealStation(station);
  }
</script>

<nav class="bridge-navigator" aria-label="All nine Miami River bridge stations">
  <div class="bridge-navigator__direction" aria-hidden="true">
    <span>UPRIVER</span><i></i><span>BAY</span>
  </div>
  <div
    class="bridge-navigator__scroller"
    bind:this={scroller}
    role="group"
    aria-label="Scrollable nine-station corridor, upriver to Biscayne Bay"
  >
  <ol>
    {#each BRIDGE_STATIONS.slice().reverse() as station, index (station.id)}
      {@const bridge = bridgeById.get(station.id)}
      {@const position = bridge ? bridgePositionCopy[bridge.state] : "POSITION NOT CONFIRMED"}
      <li
        class:bridge-navigator__station--brickell={station.id === "brickell"}
        class:bridge-navigator__station--selected={station.id === selectedBridgeId}
        class:bridge-navigator__station--up={bridge?.state === "up"}
        class:bridge-navigator__station--unknown={!bridge || bridge.state === "unknown"}
        data-bridge-navigator-id={station.id}
      >
        <button
          type="button"
          class="bridge-navigator__station-control"
          aria-label={`${station.name}, station ${index + 1} of ${BRIDGE_STATIONS.length}, ${position}. Show this bridge in the river view.`}
          aria-pressed={station.id === selectedBridgeId}
          onclick={(event) => selectStation(station.id, event.currentTarget)}
          onfocus={(event) => revealStation(event.currentTarget)}
          onkeydown={(event) => moveStationFocus(event, index)}
        >
          <span class="bridge-navigator__track" aria-hidden="true"><i></i></span>
          <span class="bridge-navigator__index" aria-hidden="true">{String(9 - index).padStart(2, "0")}</span>
          <strong>{station.shortName}</strong>
          <span>{position}</span>
        </button>
      </li>
    {/each}
  </ol>
  </div>
</nav>
